import { NestedTreeControl } from '@angular/cdk/tree';
import { Component, OnInit, inject } from '@angular/core';
import {
    MatTreeNestedDataSource,
    MatTree,
    MatTreeNodeDef,
    MatTreeNode,
    MatNestedTreeNode,
    MatTreeNodeToggle,
    MatTreeNodeOutlet,
} from '@angular/material/tree';
import {
    GrowInAnimation,
    GrowOutAnimation,
    HeightInAnimation,
    HeightOutAnimation,
    PrologueComponent,
    LoadingSlateComponent,
    SeparatorComponent,
    HighlightTextPipe,
} from '@exl-ng/mulo-common';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { ConfigurationHandlerService } from '../core/configuration-handler.service';
import { SEOService } from '../core/seo.service';
import { SearchEntities } from '../shared/configurations/portal-search.constant';
import { State } from '../shared/configurations/state.constant';
import { UrlUtils } from '../shared/utils/url.utils';
import { ResearchUnitsService } from './research-units.service';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { debounceTime } from 'rxjs';
import { RouterLink } from '@angular/router';
import {
    EspMatTreeNode,
    EspNodeContentDirective,
    EspNestedTreeNode,
} from '../shared/directives/a11y/esp-tree/esp-tree-node.directive';
import { EspMatTreeDirective } from '../shared/directives/a11y/esp-tree/esp-tree.directive';
import { MatInput } from '@angular/material/input';
import {
    MatFormField,
    MatLabel,
    MatSuffix,
} from '@angular/material/form-field';
import { MatButton, MatIconButton } from '@angular/material/button';

import { SvgViewboxDirective } from '@exl-ng/mulo-core';
import { MatIcon } from '@angular/material/icon';
import { CenteredBlockLayoutComponent } from '@exl-ng/mulo-views';

export interface UnitNode {
    value: {
        id: string;
        code: string;
        type: string;
        parentId: string;
        displayName: string;
        displayHomePage: boolean;
    };
    children: UnitNode[];
    level?: number;
    index?: number;
}

@Component({
    selector: 'esp-research-units',
    templateUrl: './research-units.component.html',
    styleUrls: ['./research-units.component.scss'],
    animations: [
        GrowInAnimation,
        GrowOutAnimation,
        HeightInAnimation,
        HeightOutAnimation,
    ],
    imports: [
        PrologueComponent,
        CenteredBlockLayoutComponent,
        MatIcon,
        SvgViewboxDirective,
        LoadingSlateComponent,
        MatButton,
        SeparatorComponent,
        MatFormField,
        MatLabel,
        MatInput,
        FormsModule,
        ReactiveFormsModule,
        MatSuffix,
        EspMatTreeDirective,
        MatTree,
        MatTreeNodeDef,
        EspMatTreeNode,
        MatTreeNode,
        EspNodeContentDirective,
        RouterLink,
        EspNestedTreeNode,
        MatNestedTreeNode,
        MatIconButton,
        MatTreeNodeToggle,
        MatTreeNodeOutlet,
        TranslateModule,
        HighlightTextPipe,
    ],
})
export class ResearchUnitsComponent implements OnInit {
    private orgUnitsSvc = inject(ResearchUnitsService);
    private translate = inject(TranslateService);
    private configurationHandlerService = inject(ConfigurationHandlerService);
    private seoService = inject(SEOService);

    institutionCode = UrlUtils.getParam('institution');

    treeCtrl = new NestedTreeControl<UnitNode>((node) => node.children);
    dataSource = new MatTreeNestedDataSource<UnitNode>();
    dataNodes: UnitNode[];
    unitNodes: UnitNode[];

    treeExpanded = false;
    findControl = new FormControl<string>('');
    showFindBox = false;
    loading = true;

    ngOnInit(): void {
        this.showFindBox =
            this.configurationHandlerService.portalOrgTreeConfig()?.generalConfiguration?.displayTreeSearch;

        this.changeDocumentTitle();
        this.orgUnitsSvc.getOrgUnits().subscribe((data: UnitNode[]) => {
            this.unitNodes = data as UnitNode[];
            this.unitNodes.forEach((unit) => {
                unit.level = 0;
            });
            this.sortUnitsWhenTranslationsReady(
                this.unitNodes,
                this.dataSource,
            );
            this.dataNodes = data.filter((node) => node.children?.length > 0);
            this.loading = false;
        });

        this.findControl.valueChanges
            .pipe(debounceTime(800))
            .subscribe((_) => this.handleFindChange(_));
    }

    changeDocumentTitle() {
        this.translate
            .get('research.units.document.title', {
                value: this.configurationHandlerService.mainInstitutionName(),
            })
            .subscribe((res) => {
                this.seoService.updateTitle(res);
            });
    }

    sortUnitsWhenTranslationsReady(
        units: UnitNode[],
        dataSource: MatTreeNestedDataSource<UnitNode>,
    ) {
        this.translate.get('key').subscribe((data) => {
            this.sortNodesAndChildren(units);
            dataSource.data = units;
        });
    }

    hasChildNew = (_: number, node: UnitNode) => node.children?.length > 0;

    unitSearchLink(node = { displayHomePage: false, code: '' }) {
        switch (this.orgUnitsSvc.defaultSections[0]) {
            case SearchEntities.RESEARCHERS_ENTITY:
                return [State.RESEARCHERS_SEARCH];
            case SearchEntities.OUTPUTS_ENTITY:
                return [State.OUTPUTS_SEARCH];
            case SearchEntities.PROJECTS_ENTITY:
                return [State.PROJECTS_SEARCH];
            default:
                return [
                    node.displayHomePage ? node.code : State.OUTPUTS_SEARCH,
                ];
        }
    }

    unitLinkQueryParamsNew(node: UnitNode) {
        const entity = this.orgUnitsSvc.defaultSections[0];
        return this.orgUnitsSvc.unitLinkQueryParams(
            node.value.code,
            entity === 'homepage' && !node?.value?.displayHomePage
                ? SearchEntities.OUTPUTS_ENTITY
                : entity,
        );
    }

    sortNodesAndChildren(nodes: UnitNode[]) {
        nodes.sort((a, b) =>
            this.orgUnitsSvc.alphabeticallySortAfterTranslation(
                a.value.code,
                b.value.code,
            ),
        );
        nodes.forEach((node) => {
            if (node.children.length > 0) {
                this.sortNodesAndChildren(node.children);
            }
        });
    }

    handleExpandCollapse() {
        const treeCtrl = this.treeCtrl;
        treeCtrl.dataNodes = this.dataNodes;

        if (this.treeExpanded) {
            treeCtrl?.collapseAll();
        } else {
            treeCtrl?.expandAll();
        }
        this.treeExpanded = !this.treeExpanded;
    }

    handleFindChange(data?) {
        this.treeExpanded = true;
        this.recursivelySearch(data, this.dataNodes);
        setTimeout(() =>
            document
                .getElementsByClassName('highlight')
                .item(0)
                ?.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center',
                }),
        );
    }

    recursivelySearch(searchParam: string, nodes: UnitNode[]) {
        let shouldExpand = false;
        for (const node of nodes) {
            if (node.children.length > 0) {
                shouldExpand = this.recursivelySearch(
                    searchParam,
                    node.children,
                );
                if (shouldExpand) {
                    this.treeCtrl.expand(node);
                } else if (!this.treeCtrl.isExpanded(node)) {
                    this.treeExpanded = false;
                }
            }
            if (
                searchParam?.length > 0 &&
                node.value.displayName
                    .toLowerCase()
                    .includes(searchParam.toLowerCase())
            ) {
                shouldExpand = true;
            }
        }
        return shouldExpand;
    }
}
